import { useCallback, useState } from "react";
import { checkPinnedScenario, pinScenario, unpinScenario } from "../services/PinScenarioServices";

/**
 * Pin/unpin state for a scenario card's options menu.
 *
 * Pin status is fetched lazily via loadPinStatus — call it when the options
 * menu opens — so rendering a list of cards doesn't fire one request per card.
 *
 * @param {string} scenarioId
 * @param {Function} [onPinChange] - Called after a successful pin or unpin.
 */
const useScenarioPin = (scenarioId, onPinChange) => {
    const [isPinned, setIsPinned] = useState(null); // null = status not loaded yet
    const [errorMessage, setErrorMessage] = useState(null);

    const loadPinStatus = useCallback(async () => {
        if (!scenarioId) return;

        try {
            const data = await checkPinnedScenario(scenarioId);
            setIsPinned(data.isPinned);
        } catch (error) {
            console.error("Error checking pinned scenario:", error);
        }
    }, [scenarioId]);

    const onPinScenario = async () => {
        if (isPinned === null) return; // status still loading; ignore the click

        try {
            if (isPinned) {
                await unpinScenario(scenarioId);
                setIsPinned(false);
            } else {
                await pinScenario(scenarioId);
                setIsPinned(true);
            }
            onPinChange?.();
        } catch (error) {
            console.error("Error updating pin:", error);

            if (error.response?.status === 409) {
                // Already pinned (e.g. from another tab) — just sync up
                setIsPinned(true);
            } else {
                // Surfaces the 3-pin limit message and any other failure
                setErrorMessage(error.response?.data?.message || "Unable to update pin.");
            }
        }
    };

    return {
        isPinned,
        loadPinStatus,
        onPinScenario,
        errorMessage,
        setErrorMessage
    };
};

export default useScenarioPin;
